<script setup>
import { ref, computed } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

/** Textos pt_BR (bloco de inboxes do editor de fluxo). */
const INBOX_LABEL_TITLE = 'Caixas de entrada';
const INBOX_LABEL_HINT =
  'Use "Todas as caixas" para não restringir, ou escolha uma ou mais caixas. As linhas selecionadas exibem um check.';
const INBOX_OPTION_ALL = 'Todas as caixas';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    required: true,
  },
  /** Prefixo único para ids de acessibilidade (ex.: `msg-${nodeId}`). */
  inputIdPrefix: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);

const selectedIds = computed(() => {
  const raw = props.modelValue || [];
  return new Set(raw.map(id => Number(id)));
});

const inboxById = id => props.inboxes.find(i => Number(i.id) === Number(id));

const channelIconInbox = inbox => ({
  channel_type: inbox.channel_type,
  provider: inbox.provider,
  medium: inbox.medium,
});

/** Ícone do canal só quando há exatamente uma caixa (evita ambiguidade com várias). */
const summaryPrimaryInbox = computed(() => {
  const ids = props.modelValue || [];
  if (ids.length !== 1) return null;
  return inboxById(ids[0]) || null;
});

const summaryText = computed(() => {
  const n = selectedIds.value.size;
  if (n === 0) return INBOX_OPTION_ALL;
  if (n === 1) {
    const inbox = summaryPrimaryInbox.value;
    return inbox?.name || `#${[...selectedIds.value][0]}`;
  }
  return `${n} caixas selecionadas`;
});

const toggleOpen = () => {
  open.value = !open.value;
};

const close = () => {
  open.value = false;
};

const clearSelection = () => {
  emit('update:modelValue', []);
};

const toggleInboxId = inboxId => {
  const id = Number(inboxId);
  const cur = new Set(selectedIds.value);
  if (cur.has(id)) {
    cur.delete(id);
  } else {
    cur.add(id);
  }
  emit(
    'update:modelValue',
    [...cur].sort((a, b) => a - b)
  );
};

const isSelected = id => selectedIds.value.has(Number(id));

const listboxId = computed(() => `flow-inbox-list-${props.inputIdPrefix}`);
const buttonId = computed(() => `flow-inbox-btn-${props.inputIdPrefix}`);
</script>

<template>
  <div v-on-click-outside="close" class="relative">
    <label
      class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
      :for="buttonId"
    >
      {{ INBOX_LABEL_TITLE }}
    </label>
    <p class="mb-2 text-xs leading-snug text-n-slate-11">
      {{ INBOX_LABEL_HINT }}
    </p>
    <button
      :id="buttonId"
      type="button"
      class="flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-left text-sm text-n-slate-12 outline-none transition-colors hover:border-n-slate-7 focus-visible:ring-2 focus-visible:ring-n-teal-9"
      :class="open ? 'ring-2 ring-n-teal-9 border-transparent' : ''"
      :aria-expanded="open"
      :aria-controls="listboxId"
      aria-haspopup="listbox"
      @click="toggleOpen"
    >
      <span class="flex min-w-0 flex-1 items-center gap-2">
        <ChannelIcon
          v-if="summaryPrimaryInbox"
          :inbox="channelIconInbox(summaryPrimaryInbox)"
          class="size-4 shrink-0 text-n-slate-11"
        />
        <span
          v-else
          class="i-lucide-inbox size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
        <span class="min-w-0 truncate font-medium">
          {{ summaryText }}
        </span>
      </span>
      <span
        class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11 transition-transform"
        :class="open ? 'rotate-180' : ''"
        aria-hidden="true"
      />
    </button>

    <div
      v-show="open"
      :id="listboxId"
      class="absolute left-0 right-0 top-full z-50 mt-1 max-h-52 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
      role="listbox"
      :aria-labelledby="buttonId"
      tabindex="-1"
    >
      <button
        type="button"
        role="option"
        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
        :aria-selected="selectedIds.size === 0"
        @click="
          clearSelection();
          close();
        "
      >
        <span
          class="i-lucide-inbox size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
        <span class="min-w-0 flex-1 truncate">
          {{ INBOX_OPTION_ALL }}
        </span>
      </button>
      <button
        v-for="inbox in inboxes"
        :key="`flow-inbox-opt-${inputIdPrefix}-${inbox.id}`"
        type="button"
        role="option"
        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
        :class="isSelected(inbox.id) ? 'bg-n-alpha-3' : ''"
        :aria-selected="isSelected(inbox.id)"
        @click="toggleInboxId(inbox.id)"
      >
        <ChannelIcon
          :inbox="channelIconInbox(inbox)"
          class="size-4 shrink-0 text-n-slate-11"
        />
        <span class="min-w-0 flex-1 truncate">
          {{ inbox.name }}
        </span>
        <span
          v-if="isSelected(inbox.id)"
          class="i-lucide-check size-4 shrink-0 text-n-teal-11"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>
